/**
 * GestorCuentaCorrientePage — Vista del Gestor: lo que adeuda al CEO.
 *
 * Muestra:
 *   - Saldo pendiente al CEO (lo que debe rendir)
 *   - Su ganancia acumulada (informativo, ya está en su wallet)
 *   - Botón "Declarar depósito" para rendir
 *   - Tabla de movimientos recientes
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Banknote, Send, CheckCircle2, Clock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { extractErrorMessage } from '@/utils/errors';
import { Money } from '@/components/Money';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENTRY_LABEL = {
  cobranza_ganancia_ceo:     { label: 'Cobranza · adeudado',  icon: '📥', cls: 'bg-amber-50 text-amber-800' },
  cobranza_ganancia_gestor:  { label: 'Tu ganancia',           icon: '💚', cls: 'bg-emerald-50 text-emerald-800' },
  rendicion:                 { label: 'Rendición declarada',   icon: '🕓', cls: 'bg-slate-50 text-slate-700' },
  rendicion_confirmada:      { label: 'Rendición confirmada',  icon: '✅', cls: 'bg-emerald-50 text-emerald-800' },
};

const GestorCuentaCorrientePage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [form, setForm] = useState({ amount_usdt: '', moneda_origen: 'UYU', monto_origen: '', referencia: '', nota: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/gestor/me/cuenta-corriente`, { headers: getAuthHeaders() });
      setData(r.data);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'No se pudo cargar'));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const onDeclare = async () => {
    if (!form.amount_usdt || Number(form.amount_usdt) <= 0) {
      toast.error('Ingresá el monto en USD que vas a depositar');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/gestor/me/cuenta-corriente/rendicion`, {
        amount_usdt: Number(form.amount_usdt),
        moneda_origen: form.moneda_origen,
        monto_origen: form.monto_origen ? Number(form.monto_origen) : null,
        referencia: form.referencia || null,
        nota: form.nota || null,
      }, { headers: getAuthHeaders() });
      toast.success('Rendición declarada — esperando confirmación del CEO');
      setOpenModal(false);
      setForm({ amount_usdt: '', moneda_origen: 'UYU', monto_origen: '', referencia: '', nota: '' });
      await load();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'No se pudo registrar'));
    } finally { setSaving(false); }
  };

  if (loading || !data) return <p className="text-center text-slate-500 py-12">Cargando…</p>;
  const k = data.kpis;

  return (
    <div className="max-w-5xl mx-auto space-y-4" data-testid="gestor-cuenta-corriente-page">
      <Card className="p-5 bg-gradient-to-br from-amber-50 to-rose-50 border-amber-200">
        <h2 className="text-xl font-bold text-slate-900">Cuenta corriente con el CEO</h2>
        <p className="text-sm text-slate-700 mt-1">
          Lo que cobraste en remesas se reparte automáticamente: el proveedor cobra su parte,
          a vos te queda tu ganancia y una porción adicional queda como <b>saldo adeudado al CEO</b>.
          Cuando hagas tu depósito al CEO, declaralo acá para que él lo confirme y reduzca tu pendiente.
        </p>
      </Card>

      {/* Hero — Saldo pendiente */}
      <Card className="p-6 border-0 shadow-md">
        <p className="text-xs uppercase tracking-wide text-rose-700 font-bold">Saldo pendiente al CEO</p>
        <div className="text-4xl font-bold tracking-tight mt-1">
          <Money amount={k.saldo_pendiente_ceo} currency="USDT" role={user?.role} sign="hide" bold />
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Fórmula: ganancia CEO acumulada − depósitos ya confirmados.
        </p>
        {k.saldo_pendiente_ceo > 0 && (
          <Button
            onClick={() => setOpenModal(true)}
            className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="declarar-rendicion-btn"
          >
            <Banknote className="w-4 h-4 mr-2" /> Declarar depósito al CEO
          </Button>
        )}
      </Card>

      {/* KPIs secundarios */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 border-0 shadow-sm">
          <p className="text-[10px] uppercase text-slate-500">Tu ganancia acumulada</p>
          <Money amount={k.ganancia_gestor_acumulada} currency="USDT" role={user?.role} sign="hide" bold className="text-lg" />
        </Card>
        <Card className="p-4 border-0 shadow-sm">
          <p className="text-[10px] uppercase text-slate-500">Adeudado al CEO total</p>
          <Money amount={k.ganancia_ceo_acumulada} currency="USDT" role={user?.role} sign="hide" bold className="text-lg" />
        </Card>
        <Card className="p-4 border-0 shadow-sm">
          <p className="text-[10px] uppercase text-slate-500">Ya rendido al CEO</p>
          <Money amount={k.depositos_confirmados} currency="USDT" role={user?.role} sign="hide" bold className="text-lg" />
        </Card>
        <Card className="p-4 border-0 shadow-sm">
          <p className="text-[10px] uppercase text-slate-500">Deuda proveedor (info)</p>
          <Money amount={k.deuda_proveedor_informativa} currency="USDT" role={user?.role} sign="hide" bold className="text-lg" />
        </Card>
      </div>

      {/* Movimientos */}
      <Card className="p-5 border-0 shadow-sm">
        <h3 className="text-base font-bold text-slate-900 mb-3">Movimientos recientes</h3>
        {data.movements.length === 0 ? (
          <p className="text-sm text-slate-500 italic">Sin movimientos aún.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase text-slate-500 border-b">
                  <th className="py-2">Fecha</th>
                  <th>Tipo</th>
                  <th className="text-right">Monto (USD)</th>
                  <th>Descripción</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {data.movements.map((m) => {
                  const meta = ENTRY_LABEL[m.entry_type] || { label: m.entry_type, icon: '·', cls: '' };
                  return (
                    <tr key={m.id} className="border-b last:border-0">
                      <td className="py-2 text-xs text-slate-600">{(m.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                      <td>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${meta.cls}`}>
                          {meta.icon} {meta.label}
                        </span>
                      </td>
                      <td className="text-right font-mono">
                        <Money amount={m.amount_usdt} currency="USDT" role={user?.role} sign="hide" />
                      </td>
                      <td className="text-xs text-slate-600">{m.description || m.referencia || '—'}</td>
                      <td>
                        {m.entry_type === 'rendicion' && m.status === 'pending' && (
                          <span className="text-amber-700 text-xs flex items-center gap-1"><Clock className="w-3 h-3" /> Esperando CEO</span>
                        )}
                        {m.entry_type === 'rendicion_confirmada' && (
                          <span className="text-emerald-700 text-xs flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Confirmada</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal Declarar Rendición */}
      <Dialog open={openModal} onOpenChange={setOpenModal}>
        <DialogContent data-testid="rendicion-modal">
          <DialogHeader>
            <DialogTitle>Declarar depósito al CEO</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Monto en USD (= USDT)</Label>
              <Input
                type="number" step="any" min="0"
                value={form.amount_usdt}
                onChange={(e) => setForm((f) => ({ ...f, amount_usdt: e.target.value }))}
                placeholder="Ej: 0.886"
                data-testid="rendicion-amount-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">Es el saldo que liquidaste al CEO.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Moneda real depositada</Label>
                <select
                  value={form.moneda_origen}
                  onChange={(e) => setForm((f) => ({ ...f, moneda_origen: e.target.value }))}
                  className="w-full border rounded-md h-10 px-2 text-sm"
                  data-testid="rendicion-currency-select"
                >
                  <option>UYU</option>
                  <option>USD</option>
                  <option>USDT</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Monto en moneda real</Label>
                <Input
                  type="number" step="any"
                  value={form.monto_origen}
                  onChange={(e) => setForm((f) => ({ ...f, monto_origen: e.target.value }))}
                  placeholder="Ej: 37.50"
                  data-testid="rendicion-monto-origen"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Referencia / Nº de transferencia</Label>
              <Input
                value={form.referencia}
                onChange={(e) => setForm((f) => ({ ...f, referencia: e.target.value }))}
                placeholder="Ej: TRX-2026-0001"
                data-testid="rendicion-referencia"
              />
            </div>
            <div>
              <Label className="text-xs">Nota (opcional)</Label>
              <Input
                value={form.nota}
                onChange={(e) => setForm((f) => ({ ...f, nota: e.target.value }))}
                placeholder="Aclaración"
                data-testid="rendicion-nota"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenModal(false)}>Cancelar</Button>
            <Button onClick={onDeclare} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? 'Enviando…' : <><Send className="w-4 h-4 mr-2" /> Declarar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestorCuentaCorrientePage;
